import React, { useEffect, useRef, useState } from "react";
import TableRow from "./TableRow";
import { TableColumn, useTableGridStyle } from "./TableContainer";
import Chevron from "../../assets/icons/Chevron";
import EmptyTableRow from "./EmptyTableRow";
import useModal from "../../utils/hooks/useModal";
import { ModalType } from "../../utils/store/reducer/slices/modalReducer";

export type TableDropdownOption<T> = {
  content: JSX.Element;
  value: T;
};

const TableDropdown = <T extends any>({
  name,
  value,
  placeholder,
  options,
  columns,
  className,
  onChange,
  onSubmit
}: {
  name: string;
  value: string;
  placeholder?: string;
  options: TableDropdownOption<T>[];
  columns: TableColumn[];
  className?: string;
  onChange: (value: string) => void;
  onSubmit: (value: T) => void;
}) => {
  const style = useTableGridStyle(columns);
  const ref = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { modal, setModal, preventClose } = useModal(ModalType.DROPDOWN, name);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Submit selected option on Enter
    if (e.key === "Enter" && options.length > 0) {
      onSubmit(options[selectedIndex].value);
      setModal(false);
    }
    // Navigate between options with arrow keys
    else if (e.key === "ArrowUp" && selectedIndex > 0) {
      setSelectedIndex(selectedIndex => selectedIndex - 1);
    } else if (e.key === "ArrowDown" && selectedIndex < options.length - 1) {
      setSelectedIndex(selectedIndex => selectedIndex + 1);
    }

    // Prevent form submission
    // e.preventDefault();
    // e.stopPropagation();

  };

  useEffect(() => {
    if (options.length > 0) setSelectedIndex(0);
  }, [options]);

  return (
    <article className={className + " relative"}>
      <div className="relative z-0 flex items-center rounded border border-gray-300">
        <input
          ref={ref}
          type="text"
          value={value}
          placeholder={placeholder}
          onFocus={e => e.preventDefault()}
          onBlur={e => e.preventDefault()}
          onClick={() => {
            setModal(true);
            preventClose();
          }}
          onChange={e => {
            onChange(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          className="flex-grow rounded px-3 py-[10px] leading-[150%]"
        />

        <Chevron
          className={
            "absolute right-3 w-5 stroke-gray-700 transition " +
            (modal ? "-rotate-180" : "rotate-0")
          }
        />
      </div>

      {modal && (
        <div className="relative z-30 mt-1 rounded-lg bg-white shadow-lg transition">
          {options.map((option, index) => (
            <TableRow
              key={index}
              onClick={() => {
                onSubmit(option.value);
                setModal(false);
              }}
              style={style}
              className="rounded-lg"
              selected={selectedIndex === index}
            >
              {option.content}
            </TableRow>
          ))}

          {options.length === 0 && <EmptyTableRow message="No results" />}
        </div>
      )}
    </article>
  );
};

export default TableDropdown;
